import { Button } from './Button'

interface Plan {
  id: string
  name: string
  price: string
  period?: string
  tagline: string
  features: string[]
  highlight?: boolean
}

const PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$495',
    period: '/month',
    tagline: 'For solo conveyancers just getting going',
    features: [
      'Up to 10 active matters',
      'Up to 25 clients',
      'Matter & task tracking',
      'Client self-service invite links',
      'Email support',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$995',
    period: '/month',
    tagline: 'Best value for growing firms',
    features: [
      'Unlimited matters & clients',
      'Everything in Starter',
      'Take client payments (Pinch)',
      'AI assistant',
      'Priority support',
    ],
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$1495',
    period: '/month',
    tagline: 'For larger firms and teams',
    features: [
      'Everything in Professional',
      'Multiple staff seats',
      'Custom task templates',
      'API access',
      'Dedicated account manager',
    ],
  },
]

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-600 mt-0.5 shrink-0">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
)

interface PlanSelectorProps {
  onSelect: (planId: string) => void
}

export const PlanSelector: React.FC<PlanSelectorProps> = ({ onSelect }) => {
  return (
    <div>
      <p className="text-sm text-gray-600 mb-6">
        Pick the plan that fits your firm — you can change this later in Settings.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative rounded-lg border p-5 flex flex-col ${
              plan.highlight ? 'border-primary ring-2 ring-accent/20' : 'border-gray-200'
            }`}
          >
            {plan.highlight && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                Best Value
              </span>
            )}

            <h3 className="text-lg font-semibold text-gray-900 mt-2">{plan.name}</h3>
            <p className="text-sm text-gray-500 mb-3">{plan.tagline}</p>

            <div className="mb-4">
              <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
              {plan.period && <span className="text-gray-500 text-sm">{plan.period}</span>}
            </div>

            <ul className="space-y-2 mb-6 flex-1">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckIcon />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              type="button"
              variant={plan.highlight ? 'primary' : 'secondary'}
              onClick={() => onSelect(plan.id)}
            >
              Choose {plan.name}
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}
